import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { RoleCode, UserInterface } from '@/interfaces/ServerInterfaces'

import APIServices from '../../api/APIServices'

// Create a unique, descriptive key for this query
const BUSINESS_USER_QUERY_KEY = 'business-user'

/**
 * Fetch business user from the API
 * Separating API call logic from the hook for better testability
 */
const fetchBusinessUser = async (): Promise<UserInterface> => {
  const response = await APIServices.get('/security/user')
  return response.data
}

/**
 * Custom hook for fetching business user
 *
 * @param params - Query parameters including businessId
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useBusinessUserQuery = ({
  businessId,
  options,
}: {
  businessId: number
  options?: Partial<UseQueryOptions<UserInterface, AxiosError>>
}) => {
  // Query hook
  const queryResult = useQuery<UserInterface, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: getBusinessUserQueryKey({ businessId }),

    // Separate the API call logic from the query configuration
    queryFn: () => fetchBusinessUser(),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  const allowRoles = (allowedRoles: RoleCode[], strict?: boolean) => {
    const userRoles: RoleCode[] =
      queryResult.data?.roles.map((item) => item.code) ?? []
    if (strict) {
      return userRoles.some((role) => allowedRoles.includes(role))
    }
    return userRoles.some((role) => ['OWNER', ...allowedRoles].includes(role))
  }

  return {
    user: queryResult.data,
    allowRoles,
    isLoadingUser: queryResult.isLoading,
    isErrorUser: queryResult.isError,
    errorUser: queryResult.error,
    refetchUser: queryResult.refetch,
    isFetchingUser: queryResult.isFetching,
  }
}

/**
 * Helper function to get the business user query key
 * Useful for cache invalidation or prefetching
 */
export const getBusinessUserQueryKey = ({
  businessId,
}: {
  businessId: number
}) => [BUSINESS_USER_QUERY_KEY, { businessId }]
